/**
 * Sticky CTA bar controller.
 *
 * Arms (see snippets/sticky-cta.liquid for the full brief):
 *   control — legacy timing. The anchor "not intersecting" shows the bar, which
 *             includes the entire scroll ABOVE the buy box.
 *   below   — the bar appears only once the anchor has scrolled off the TOP.
 *   offer   — same timing as `below`, plus the offer layout whose CTA returns
 *             the shopper to the quantity tiles instead of adding blind.
 *
 * Intelligems flips arms at runtime: window.Roostys.stickyBar.set('offer').
 */
(function () {
  const DEFAULT_THRESHOLD = 2.5;
  const MIN_THRESHOLD = 0.5;
  const MAX_THRESHOLD = 10;
  const MOBILE_MAX_WIDTH = 767;
  const TABLET_MAX_WIDTH = 1024;

  // How early the challengers arrive, as a fraction of the viewport: the bar
  // appears once the add-to-cart button's bottom edge rises into the top
  // LEAD_RATIO of the screen, rather than waiting for it to leave completely.
  // Tunable per page with ?lead=<px> while QA'ing the feel.
  const LEAD_RATIO = 0.2;
  // Flicker guard for a shopper parked exactly on the boundary. Not a delay,
  // and deliberately not the data-sticky-cta-offset-* values, which are a
  // rootMargin for control's IntersectionObserver and mean something else.
  const SHOW_PAST_PX = 8;
  const HIDE_BACK_PX = 4;

  const VARIANTS = ["control", "reentry", "select", "stepper"];

  function parseNumber(value) {
    if (value == null || value === "") return null;
    const normalized = String(value).trim().replace(",", ".");
    const num = parseFloat(normalized, 10);
    if (!Number.isFinite(num)) return null;
    return num;
  }

  function parseThreshold(value) {
    const num = parseNumber(value);
    if (num === null || num < MIN_THRESHOLD || num > MAX_THRESHOLD) return null;
    return num;
  }

  function parseOffset(value, fallback) {
    const parsed = parseNumber(value);
    if (parsed === null) return fallback;
    return Math.max(0, Math.round(parsed));
  }

  function getBreakpointOffset(stickyCta) {
    const desktopOffset = parseOffset(stickyCta.getAttribute("data-sticky-cta-offset-desktop"), 0);
    const tabletOffset = parseOffset(stickyCta.getAttribute("data-sticky-cta-offset-tablet"), desktopOffset);
    const mobileOffset = parseOffset(stickyCta.getAttribute("data-sticky-cta-offset-mobile"), tabletOffset);

    if (window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches) {
      return mobileOffset;
    }

    if (window.matchMedia(`(max-width: ${TABLET_MAX_WIDTH}px)`).matches) {
      return tabletOffset;
    }

    return desktopOffset;
  }

  function isAtPageTop() {
    return (window.scrollY || window.pageYOffset || 0) <= 0;
  }

  function initStickyCta() {
    const stickyCta = document.querySelector("[data-sticky-cta]");
    if (!stickyCta) return;

    const targetSelector = stickyCta.getAttribute("data-sticky-cta-target-selector");
    const clickSelector = stickyCta.getAttribute("data-sticky-cta-click-selector");
    let observer = null;
    let fallbackScrollHandler = null;
    let fallbackResizeHandler = null;
    let topGuardScrollHandler = null;
    let directionScrollHandler = null;
    let fallbackThresholdPx = 0;
    let anchorOutOfView = false;
    // Declared with the other state: closeDd() is reachable from
    // setStickyVisibility, which runs before the listbox block executes.
    let ddOpen = false;
    let ddActive = -1;
    let variant = readVariant();

    /**
     * Precedence: ?sticky= wins, then an Intelligems assignment, then the
     * section setting.
     *
     * The URL override exists for QA — it is the only way to switch arms on a
     * real phone, where there is no console. It changes nothing but which
     * layout this one page render draws; it does not touch the Intelligems
     * assignment, so a shared link cannot move anyone between test groups.
     */
    function readVariant() {
      const ns = window.Roostys || {};
      let fromUrl = null;
      try {
        fromUrl = new URLSearchParams(window.location.search).get("sticky");
      } catch (e) {
        fromUrl = null;
      }
      const fromGlobal = typeof ns.stickyBarVariant === "string" ? ns.stickyBarVariant : null;
      const fromAttr = stickyCta.getAttribute("data-sticky-cta-variant");
      const chosen = fromUrl || fromGlobal || fromAttr || "control";
      return VARIANTS.indexOf(chosen) === -1 ? "control" : chosen;
    }

    /** Every challenger uses the corrected, direction-aware trigger. */
    function usesDirectionalTrigger() {
      return variant !== "control";
    }

    function anchor() {
      return targetSelector ? document.querySelector(targetSelector) : null;
    }

    /** The real submit button, whose disabled/loading state the bar mirrors. */
    function realSubmitButton() {
      const byClick = clickSelector ? document.querySelector(clickSelector) : null;
      if (byClick) return byClick;
      return (targetSelector ? document.querySelector(targetSelector) : null) || null;
    }

    function proxiedButtons() {
      return stickyCta.querySelectorAll("[data-sticky-add]");
    }

    function applyStickyInteractionState(isVisible) {
      stickyCta.style.pointerEvents = isVisible ? "auto" : "none";
      stickyCta.setAttribute("aria-hidden", isVisible ? "false" : "true");
      // The bar hides with a transform, so its button stays in the tab order
      // unless it is explicitly removed. aria-hidden alone would leave sighted
      // keyboard users focusing an invisible, still-activatable control.
      if ("inert" in HTMLElement.prototype) {
        stickyCta.inert = !isVisible;
      } else {
        stickyCta.querySelectorAll("button, a, input, select, textarea").forEach(function (el) {
          if (isVisible) {
            if (el.dataset.stickyPrevTabindex === undefined) el.removeAttribute("tabindex");
            else el.setAttribute("tabindex", el.dataset.stickyPrevTabindex);
          } else {
            if (el.hasAttribute("tabindex")) el.dataset.stickyPrevTabindex = el.getAttribute("tabindex");
            el.setAttribute("tabindex", "-1");
          }
        });
      }
    }

    let lastVisible = null;

    function setStickyVisibility(shouldShow) {
      const isVisible = shouldShow && !isAtPageTop();
      if (isVisible === lastVisible) return;
      lastVisible = isVisible;

      if (isVisible) {
        stickyCta.classList.add("active");
        reserveSpace();
      } else {
        stickyCta.classList.remove("active");
      }
      document.body.classList.toggle("sticky-cta-active", isVisible);
      if (!isVisible && typeof closeDd === "function") closeDd(false);

      applyStickyInteractionState(isVisible);
    }

    function updateVisibilityFromAnchor() {
      setStickyVisibility(anchorOutOfView);
    }

    function initTopGuardListener() {
      if (topGuardScrollHandler) return;

      topGuardScrollHandler = function () {
        updateVisibilityFromAnchor();
      };

      window.addEventListener("scroll", topGuardScrollHandler, { passive: true });
    }

    function clearTopGuardListener() {
      if (!topGuardScrollHandler) return;
      window.removeEventListener("scroll", topGuardScrollHandler);
      topGuardScrollHandler = null;
    }

    /**
     * Arms `below` / `offer`.
     *
     * IntersectionObserver cannot tell "the anchor is below the fold, the
     * shopper has not reached the buy box yet" from "the anchor is above the
     * fold, the shopper has passed it" — both are simply `!isIntersecting`,
     * which is why the legacy bar sits on screen for the whole page. Reading
     * the anchor rect gives us the sign we need.
     */
    /** Pixels of head start, from ?lead= or a fraction of the viewport. */
    function leadPx() {
      let fromUrl = null;
      try {
        fromUrl = new URLSearchParams(window.location.search).get("lead");
      } catch (e) {
        fromUrl = null;
      }
      const parsed = parseNumber(fromUrl);
      if (parsed !== null && parsed >= 0 && parsed <= 2000) return Math.round(parsed);
      return Math.round(window.innerHeight * LEAD_RATIO);
    }

    function initDirectionalTrigger() {
      function evaluate() {
        const target = anchor();
        if (!target) {
          initThresholdFallback();
          return;
        }
        // Measured from the button's bottom edge against the top of the screen.
        // The lead brings the bar in as the button rises into the top of the
        // viewport, instead of only once it has gone entirely.
        const lead = leadPx();
        const bottom = target.getBoundingClientRect().bottom - lead;
        if (bottom < -SHOW_PAST_PX) anchorOutOfView = true;
        else if (bottom > HIDE_BACK_PX) anchorOutOfView = false;
        updateVisibilityFromAnchor();
      }

      // Evaluated synchronously. An rAF latch (`scheduled = true` until the
      // frame runs) is orphaned whenever a queued frame never fires — a
      // backgrounded tab or an app switch mid-scroll — which leaves the latch
      // stuck and freezes the bar for the rest of the session. This reads one
      // rect per scroll event, which browsers already coalesce to a frame, and
      // setStickyVisibility only touches the DOM when the state actually flips.
      directionScrollHandler = evaluate;

      window.addEventListener("scroll", directionScrollHandler, { passive: true });
      window.addEventListener("resize", directionScrollHandler);
      document.addEventListener("visibilitychange", directionScrollHandler);
      evaluate();
    }

    function clearDirectionalTrigger() {
      if (!directionScrollHandler) return;
      window.removeEventListener("scroll", directionScrollHandler);
      window.removeEventListener("resize", directionScrollHandler);
      document.removeEventListener("visibilitychange", directionScrollHandler);
      directionScrollHandler = null;
    }

    function initThresholdFallback() {
      // Guard: evaluate() can reach this on every animation frame when the
      // anchor is missing, which would stack a new scroll listener each time.
      if (fallbackScrollHandler) return;
      clearDirectionalTrigger();
      const thresholdAttr = stickyCta.getAttribute("data-sticky-cta-threshold");
      const parsedThreshold = parseThreshold(thresholdAttr);
      const thresholdMultiplier = parsedThreshold !== null ? parsedThreshold : DEFAULT_THRESHOLD;

      function updateFromThreshold() {
        anchorOutOfView = window.scrollY >= fallbackThresholdPx;
        updateVisibilityFromAnchor();
      }

      fallbackScrollHandler = function () {
        fallbackThresholdPx = thresholdMultiplier * window.innerHeight;
        updateFromThreshold();
      };

      fallbackResizeHandler = fallbackScrollHandler;
      fallbackThresholdPx = thresholdMultiplier * window.innerHeight;
      window.addEventListener("scroll", fallbackScrollHandler, { passive: true });
      window.addEventListener("resize", fallbackResizeHandler);
      initTopGuardListener();
      updateFromThreshold();
    }

    function destroyObserver() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }

    function createObserver() {
      destroyObserver();
      const targetButton = anchor();

      if (!targetButton) {
        initThresholdFallback();
        return;
      }

      const offset = getBreakpointOffset(stickyCta);
      observer = new IntersectionObserver(
        function (entries) {
          const entry = entries[0];
          if (!entry) return;
          anchorOutOfView = !entry.isIntersecting;
          updateVisibilityFromAnchor();
        },
        {
          threshold: 0,
          root: null,
          rootMargin: `${offset}px 0px 0px 0px`,
        }
      );

      observer.observe(targetButton);
      initTopGuardListener();
      anchorOutOfView = false;
      updateVisibilityFromAnchor();
    }

    function clearFallbackListeners() {
      if (fallbackScrollHandler) {
        window.removeEventListener("scroll", fallbackScrollHandler);
        fallbackScrollHandler = null;
      }
      if (fallbackResizeHandler) {
        window.removeEventListener("resize", fallbackResizeHandler);
        fallbackResizeHandler = null;
      }
      fallbackThresholdPx = 0;
    }

    function rebuildVisibilityListeners() {
      clearFallbackListeners();
      clearTopGuardListener();
      clearDirectionalTrigger();
      destroyObserver();
      anchorOutOfView = false;
      lastVisible = null;

      if (usesDirectionalTrigger()) {
        initTopGuardListener();
        initDirectionalTrigger();
      } else {
        createObserver();
      }
    }

    /* ---------------------------------------------------------------- *
     * Actions
     * ---------------------------------------------------------------- */

    /** Lets Intelligems and GA read sticky-originated intent separately. */
    function emit(action) {
      const detail = { variant: variant, action: action };
      document.dispatchEvent(new CustomEvent("roostys:sticky-cta", { detail: detail, bubbles: true }));
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: "roostys_sticky_cta", sticky_variant: variant, sticky_action: action });
      }
    }

    function handleAdd() {
      const button = realSubmitButton();
      // Sold-out renders the fallback button outside <product-form>, so the
      // anchor selector finds nothing — never call .click() on null.
      if (!button || button.disabled || button.getAttribute("aria-disabled") === "true") return;
      emit("add_to_cart");
      button.click();
    }

    /**
     * First candidate that is actually rendered. A display:none element measures
     * top 0, so jumping to it scrolls nowhere — the flock-size lander hides
     * .qty-tile__list and drives it from its own .fso selector instead.
     */
    function jumpTarget() {
      // Each selector can match more than one element — the PDP carries a hidden legacy
      // picker, and a tiles A/B arm (T010) adds a second .qty-tile__list with the control's
      // hidden — so take the first RENDERED match per selector, not the first match.
      const selectors = [".fso", ".qty-tile__list", "[id^='variant-selects-']", ".variant-selects"];
      for (let i = 0; i < selectors.length; i++) {
        const matches = document.querySelectorAll(selectors[i]);
        for (let j = 0; j < matches.length; j++) {
          if (matches[j].getClientRects().length) return matches[j];
        }
      }
      const fallbacks = [realSubmitButton(), document.querySelector(".main-product-section")];
      for (let i = 0; i < fallbacks.length; i++) {
        if (fallbacks[i] && fallbacks[i].getClientRects().length) return fallbacks[i];
      }
      return null;
    }

    function handleJump() {
      const target = jumpTarget();
      if (!target) return;
      emit("jump_to_buy_box");
      const header = document.querySelector(".section-header");
      const headerOffset = header ? header.getBoundingClientRect().height : 0;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 12;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? "auto" : "smooth" });
    }

    stickyCta.addEventListener("click", function (event) {
      if (event.target.closest("[data-sticky-add]")) handleAdd();
      else if (event.target.closest("[data-sticky-jump]")) handleJump();
    });

    /**
     * Mirror the real button's disabled/loading state onto the proxy, so the
     * bar never invites a tap that silently no-ops.
     */
    function syncDisabledState() {
      const button = realSubmitButton();
      const isDisabled = !button || button.disabled || button.getAttribute("aria-disabled") === "true";
      proxiedButtons().forEach(function (el) {
        el.disabled = isDisabled;
      });
    }

    function observeSubmitState() {
      const button = realSubmitButton();
      if (!button || typeof MutationObserver === "undefined") return;
      const mo = new MutationObserver(syncDisabledState);
      mo.observe(button, { attributes: true, attributeFilter: ["disabled", "aria-disabled", "class"] });
      syncDisabledState();
    }

    /* ---------------------------------------------------------------- *
     * Pack control (arms `select` / `stepper`)
     *
     * The tiles in qty-tiles-v3 stay the single source of truth. We read the
     * tier list off them and write selections back by checking the real radio
     * and dispatching `change`, which is what buy-box-v3-scripts already
     * listens for — so pricing, the gift gate and the rest keep working
     * without a second copy of any of that logic.
     * ---------------------------------------------------------------- */

    const pickRow = stickyCta.querySelector('[data-sticky-layout="pick"]');
    const ddRoot = stickyCta.querySelector("[data-sticky-dd]");
    const ddBtn = stickyCta.querySelector("[data-sticky-dd-btn]");
    const ddList = stickyCta.querySelector("[data-sticky-dd-list]");
    const ddLabel = stickyCta.querySelector("[data-sticky-dd-label]");
    const stepLabel = stickyCta.querySelector("[data-sticky-step-label]");
    const nowEl = stickyCta.querySelector("[data-sticky-now]");
    const compareEl = stickyCta.querySelector("[data-sticky-compare]");

    const PICKER_SELECTOR = '[id^="variant-selects-"], .qty-tile__list';

    /**
     * The v3 buy box renders BOTH pickers into the DOM — the legacy
     * variant-selects list and the qty tiles — and hides one with CSS. A bare
     * `input[data-total-price]` query therefore returns every tier twice, which
     * doubled the dropdown and made the stepper walk a list whose first checked
     * entry lives in the hidden picker. Take only the visible picker, then
     * de-duplicate by variant id as a belt-and-braces guard.
     */
    function tiles() {
      const all = Array.prototype.slice
        .call(document.querySelectorAll("input[data-total-price]"))
        .filter(function (el) {
          if (el.disabled || el.classList.contains("disabled")) return false;
          const picker = el.closest(PICKER_SELECTOR);
          return !picker || window.getComputedStyle(picker).display !== "none";
        });

      const byVariant = [];
      const seen = {};
      all.forEach(function (el) {
        const key = el.getAttribute("data-variant-id") || el.getAttribute("data-title") || "";
        if (Object.prototype.hasOwnProperty.call(seen, key)) return;
        seen[key] = true;
        byVariant.push(el);
      });
      return byVariant;
    }

    function checkedTile() {
      const list = tiles();
      for (let i = 0; i < list.length; i++) {
        if (list[i].checked) return list[i];
      }
      return null;
    }

    function isSubscription() {
      const root = document.querySelector(".main-product-section");
      return !root || !root.classList.contains("has-one-time");
    }

    function tilePrice(tile) {
      return tile.getAttribute(isSubscription() ? "data-total-price-sub" : "data-total-price") || "";
    }

    function money(value) {
      if (!value) return null;
      const n = parseFloat(String(value).replace(/[^0-9.]/g, ""));
      return Number.isFinite(n) ? n : null;
    }

    /**
     * The tier's saving as a percentage off the compare-at price — the same
     * basis as the heading badge and the "Step 2: Subscribe & Save X%" line,
     * so the bar states the number the shopper has already seen (1 pouch on
     * subscription reads 27%, not the 20% carried on the tile itself).
     *
     * Computed rather than read from the one-time attributes, which do not
     * hold up: data-one-time-discount claims 40% on the 1-pouch tier, and
     * data-one-time-save-amount says $3.29 where compare-at minus price is
     * $3.00 — drifting to $42.88 against a real $44.99 on the 4-pouch tier.
     * data-today-save-pct is trustworthy and matches this calculation exactly,
     * so it is preferred for subscription where present.
     */
    function tileSavePct(tile) {
      const sub = isSubscription();
      if (sub) {
        const today = parseNumber(tile.getAttribute("data-today-save-pct"));
        if (today !== null && today > 0) return Math.round(today);
      }
      const compare = money(tile.getAttribute("data-compare-price"));
      const price = money(tile.getAttribute(sub ? "data-total-price-sub" : "data-total-price"));
      if (compare === null || price === null || compare <= price) return null;
      return Math.round(((compare - price) / compare) * 100);
    }

    /** Falls back to the price for any tier with no compare-at to save against. */
    function tileSave(tile) {
      const pct = tileSavePct(tile);
      if (pct === null) return tilePrice(tile);
      const template =
        (window.FLOCK_I18N && window.FLOCK_I18N.savePercent) || "Save __PCT__%";
      return template.replace("__PCT__", String(pct));
    }

    function buildTierOptions() {
      if (!ddList) return;
      const list = tiles();
      if (!list.length) return;
      ddList.innerHTML = "";
      list.forEach(function (tile, i) {
        const li = document.createElement("li");
        li.className = "scp-dd__opt";
        li.setAttribute("role", "option");
        li.setAttribute("aria-selected", "false");
        li.setAttribute("data-index", String(i));
        li.id = "scp-opt-" + i;

        const name = document.createElement("span");
        name.className = "scp-dd__name";
        name.textContent = tile.getAttribute("data-title") || "Option " + (i + 1);

        const price = document.createElement("span");
        price.className = "scp-dd__opt-price";
        price.textContent = tileSave(tile);

        li.appendChild(name);
        li.appendChild(price);
        ddList.appendChild(li);
      });
    }

    /** Reflect the buy box's current state into the row. Never the reverse. */
    function syncPickRow() {
      if (!pickRow) return;
      const list = tiles();
      const current = checkedTile();
      const index = current ? list.indexOf(current) : -1;

      if (ddList && ddList.children.length === list.length) {
        Array.prototype.forEach.call(ddList.children, function (li, i) {
          li.setAttribute("aria-selected", i === index ? "true" : "false");
          const priceEl = li.querySelector(".scp-dd__opt-price");
          if (priceEl && list[i]) priceEl.textContent = tileSave(list[i]);
        });
      }
      if (ddLabel && current) ddLabel.textContent = current.getAttribute("data-title") || "";
      if (stepLabel && current) {
        stepLabel.textContent = current.getAttribute("data-title") || "";
      }
      stickyCta.querySelectorAll("[data-sticky-step]").forEach(function (btn) {
        const dir = Number(btn.getAttribute("data-sticky-step"));
        btn.disabled = index < 0 || (dir < 0 ? index === 0 : index === list.length - 1);
      });

      if (current) {
        const sub = isSubscription();
        const price = current.getAttribute(sub ? "data-total-price-sub" : "data-total-price");
        const compare = current.getAttribute("data-compare-price");
        if (nowEl) nowEl.textContent = price || "";
        // The compare-at anchor the legacy bar dropped, and only when it beats
        // the live price — some cheapest tiers carry no compare_at_price.
        if (compareEl) compareEl.textContent = compare && compare !== price ? compare : "";
      }
    }

    function selectTierByIndex(next) {
      const list = tiles();
      if (next < 0 || next >= list.length) return;
      const tile = list[next];
      if (!tile || tile.checked) return;
      tile.checked = true;
      tile.dispatchEvent(new Event("change", { bubbles: true }));
      emit("change_pack");
      syncPickRow();
    }

    /* --- listbox behaviour: open upward, keyboard-operable, click-away --- */

    function setActive(i) {
      if (!ddList) return;
      const opts = ddList.children;
      if (!opts.length) return;
      ddActive = Math.max(0, Math.min(opts.length - 1, i));
      Array.prototype.forEach.call(opts, function (li, n) {
        li.classList.toggle("is-active", n === ddActive);
      });
      const active = opts[ddActive];
      if (active) {
        ddList.setAttribute("aria-activedescendant", active.id);
        if (active.scrollIntoView) active.scrollIntoView({ block: "nearest" });
      }
    }

    function openDd() {
      if (!ddList || !ddBtn || ddOpen) return;
      ddOpen = true;
      ddList.hidden = false;
      ddBtn.setAttribute("aria-expanded", "true");
      const list = tiles();
      const current = checkedTile();
      setActive(current ? list.indexOf(current) : 0);
      ddList.focus({ preventScroll: true });
    }

    function closeDd(refocus) {
      if (!ddList || !ddBtn || !ddOpen) return;
      ddOpen = false;
      ddList.hidden = true;
      ddBtn.setAttribute("aria-expanded", "false");
      if (refocus) ddBtn.focus();
    }

    if (ddBtn && ddList) {
      ddList.setAttribute("tabindex", "-1");

      ddBtn.addEventListener("click", function () {
        if (ddOpen) closeDd(false);
        else openDd();
      });

      ddList.addEventListener("click", function (event) {
        const opt = event.target.closest("[data-index]");
        if (!opt) return;
        selectTierByIndex(Number(opt.getAttribute("data-index")));
        closeDd(true);
      });

      ddList.addEventListener("keydown", function (event) {
        if (event.key === "Escape") { event.preventDefault(); closeDd(true); return; }
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          selectTierByIndex(ddActive);
          closeDd(true);
          return;
        }
        if (event.key === "ArrowDown") { event.preventDefault(); setActive(ddActive + 1); }
        else if (event.key === "ArrowUp") { event.preventDefault(); setActive(ddActive - 1); }
        else if (event.key === "Home") { event.preventDefault(); setActive(0); }
        else if (event.key === "End") { event.preventDefault(); setActive(ddList.children.length - 1); }
      });

      ddBtn.addEventListener("keydown", function (event) {
        if (event.key === "ArrowUp" || event.key === "ArrowDown") { event.preventDefault(); openDd(); }
      });

      document.addEventListener("click", function (event) {
        if (ddOpen && ddRoot && !ddRoot.contains(event.target)) closeDd(false);
      });
    }
    stickyCta.querySelectorAll("[data-sticky-step]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        const list = tiles();
        const current = checkedTile();
        const index = current ? list.indexOf(current) : -1;
        if (index < 0) return;
        selectTierByIndex(index + Number(btn.getAttribute("data-sticky-step")));
      });
    });

    document.addEventListener("change", function (event) {
      if (event.target && event.target.closest && event.target.closest(".variant-selects")) syncPickRow();
    });
    document.addEventListener("skio::update-selling-plan", syncPickRow);

    /* ---------------------------------------------------------------- *
     * Urgency line (arm `reentry`) — mirrored from the real cutoff timer
     * ---------------------------------------------------------------- */

    const urgencyEl = stickyCta.querySelector("[data-sticky-urgency]");

    function syncUrgency() {
      if (!urgencyEl) return;
      const timer = document.querySelector(".pdp-shipping-cutoff-timer");
      if (!timer) return;
      const heading = timer.querySelector(".pdp-shipping-cutoff-timer__heading");
      const countdown = timer.querySelector("[data-pdp-sct-countdown]");
      if (!countdown || !countdown.textContent.trim()) return;
      const lead = heading ? heading.textContent.replace(/\s+/g, " ").trim() : "";
      urgencyEl.innerHTML = "";
      if (lead) urgencyEl.appendChild(document.createTextNode(lead + " "));
      const strong = document.createElement("b");
      strong.textContent = countdown.textContent.replace(/\s+/g, " ").trim();
      urgencyEl.appendChild(strong);
    }

    function watchUrgency() {
      const timer = document.querySelector(".pdp-shipping-cutoff-timer");
      if (!timer || typeof MutationObserver === "undefined") return;
      new MutationObserver(syncUrgency).observe(timer, { subtree: true, childList: true, characterData: true });
      syncUrgency();
    }

    /* ---------------------------------------------------------------- *
     * Public API — the Intelligems hook
     * ---------------------------------------------------------------- */

    function applyVariant(next) {
      if (VARIANTS.indexOf(next) === -1) return variant;
      variant = next;
      window.Roostys.stickyBarVariant = next;
      stickyCta.setAttribute("data-sticky-cta-variant", next);
      syncPickRow();
      syncUrgency();
      rebuildVisibilityListeners();
      return variant;
    }

    window.Roostys = window.Roostys || {};
    window.Roostys.stickyBar = {
      get: function () {
        return variant;
      },
      set: applyVariant,
      variants: VARIANTS.slice(),
      refresh: rebuildVisibilityListeners,
    };

    /**
     * Reserve the bar's height at the foot of the document only while the bar
     * is on screen, so a fixed overlay never covers the end of the footer.
     * Applied as a class + custom property rather than permanent padding: a
     * permanent reserve would leave a white gap under the footer on every page
     * the bar is rendered on. Matches the existing listicle-bar-active pattern.
     */
    function reserveSpace() {
      const height = Math.round(stickyCta.getBoundingClientRect().height);
      if (height > 0) document.documentElement.style.setProperty("--sticky-cta-height", height + "px");
    }

    applyStickyInteractionState(false);
    variant = readVariant();
    stickyCta.setAttribute("data-sticky-cta-variant", variant);
    buildTierOptions();
    syncPickRow();
    watchUrgency();
    rebuildVisibilityListeners();
    observeSubmitState();
    window.addEventListener("resize", rebuildVisibilityListeners);
    window.addEventListener("resize", reserveSpace);
    window.addEventListener("orientationchange", rebuildVisibilityListeners);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initStickyCta);
  } else {
    initStickyCta();
  }
})();
